import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, GRAY_DARK, GRAY_DARKER, RED_LIGHT} from 'ui-library/Colors';
import DnDCanvas from 'ui-library/drag-and-drop/DnDCanvas';
import {DnDDragHandleProps, DnDDragHandleIcon} from 'ui-library/drag-and-drop/DnDDragHandle';
import {DND_BLOCK_SPACING} from 'ui-library/drag-and-drop/DnDStyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import {immutableMove} from 'util/ImmutableListUtil';

import {
  AnalysisOutputType,
  OutputDocumentContent,
  OutputDocumentLayoutConfiguration,
  OutputDocumentLayoutItem,
  OutputDocumentLayoutItemType,
  OutputDocumentSectionTitle,
} from './Types';

export const DEFAULT_NEW_SECTION_TITLE = 'New section';

type PresentationalOutputDocumentContent = Omit<OutputDocumentContent, 'outputConfig' | 'selectorIndex'> & {
  label: string;
};
type PresentationalOutputDocumentLayoutItem =
  | PresentationalOutputDocumentContent
  | OutputDocumentSectionTitle;
type PresentationalOutputDocumentLayoutDnDItem = PresentationalOutputDocumentLayoutItem & {index: number};
/** @package */
export type PresentationalOutputDocumentLayoutConfiguration = Array<PresentationalOutputDocumentLayoutItem>;

function OutputDocumentSectionTitleDnD({
  onDeleteSection,
  onUpdateSectionTitle,
  title,
  index,
  readOnly,
}: OutputDocumentSectionTitle & {
  onDeleteSection: (index: number) => void;
  onUpdateSectionTitle: (changedValue: string, index: number) => void;
  index: number;
  readOnly: boolean;
}): JSX.Element {
  return (
    <div className={css(styles.title)}>
      <EditableTextField
        extraActionButtons={{
          viewMode: [],
          editMode:
            index > 0
              ? [
                  <BenchlingIcon
                    className={classNames(css(styles.deleteIcon), 'u-danger-pointer')}
                    key="delete"
                    icon="delete"
                    onClick={() => {
                      onDeleteSection(index);
                    }}
                  />,
                ]
              : [],
        }}
        value={title}
        requestChange={(changedValue) => {
          onUpdateSectionTitle(changedValue.toString(), index);
        }}
        isDisabled={readOnly}
        dataTestAttrs={{component: 'OutputDocumentLayout', element: 'sectionTitle'}}
      />
    </div>
  );
}

function OutputDocumentContentDnD({
  icon,
  label,
  dragHandleProps,
  readOnly,
}: PresentationalOutputDocumentContent & {
  dragHandleProps: DnDDragHandleProps;
  readOnly: boolean;
}): JSX.Element {
  return (
    <div
      className={css(styles.document)}
      {...getTestProps({component: 'OutputDocumentLayout', element: 'documentContent'})}
    >
      {dragHandleProps && !readOnly && (
        <DnDDragHandleIcon
          className={classNames(css(styles.dndIcon, styles.dndInteractiveIcon), dragHandleProps.className)}
          onMouseDown={dragHandleProps.onMouseDown}
        />
      )}
      <BenchlingIcon
        icon={icon}
        className={classNames('u-small-spacing-left', css(styles.documentContentIcon))}
      />
      <span className="u-mini-spacing-left">{label}</span>
    </div>
  );
}

export interface PresentationalOutputDocumentLayoutProps {
  documentLayoutConfiguration: PresentationalOutputDocumentLayoutConfiguration;
  onAddSection: () => void;
  onUpdateSectionTitle: (title: string, index: number) => void;
  onDeleteSection: (index: number) => void;
  onMoveItem: (
    source: PresentationalOutputDocumentLayoutDnDItem,
    target: PresentationalOutputDocumentLayoutDnDItem
  ) => void;
  readOnly: boolean;
}

export function PresentationalOutputDocumentLayout({
  documentLayoutConfiguration,
  onAddSection,
  onUpdateSectionTitle,
  onDeleteSection,
  onMoveItem,
  readOnly,
}: PresentationalOutputDocumentLayoutProps): JSX.Element {
  if (documentLayoutConfiguration.length === 0) {
    return <div />;
  }
  const arrangedItems: Array<Array<PresentationalOutputDocumentLayoutDnDItem>> =
    documentLayoutConfiguration.map((item, index) => [{...item, index}]);

  return (
    <div {...getTestProps({component: 'OutputDocumentLayout', element: 'root'})}>
      <div className={css(styles.canvasWrapper)}>
        <DnDCanvas<PresentationalOutputDocumentLayoutDnDItem>
          items={arrangedItems}
          maxColumnCount={1}
          onMoveItem={onMoveItem}
          renderItem={(
            itemProps: PresentationalOutputDocumentLayoutDnDItem,
            dragHandleProps: DnDDragHandleProps
          ) => {
            if (itemProps.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
              const props = {...itemProps, onDeleteSection, onUpdateSectionTitle};
              return <OutputDocumentSectionTitleDnD {...props} readOnly={readOnly} />;
            } else {
              return (
                <OutputDocumentContentDnD
                  {...itemProps}
                  dragHandleProps={dragHandleProps}
                  readOnly={readOnly}
                />
              );
            }
          }}
          disableHorizontalDrag={true}
          disabledItems={readOnly ? arrangedItems.map((_, index) => index) : []}
        />
      </div>
      {!readOnly && (
        <AddItemButton
          label="Add Section"
          onClick={onAddSection}
          dataTestAttrs={{component: 'OutputDocumentLayout', element: 'addSectionButton'}}
        />
      )}
    </div>
  );
}

export interface OutputDocumentLayoutProps {
  documentLayoutConfiguration: OutputDocumentLayoutConfiguration;
  onSetDocumentLayoutConfiguration: (documentLayoutConfiguration: OutputDocumentLayoutConfiguration) => void;
  readOnly?: boolean;
}

export default function OutputDocumentLayout({
  documentLayoutConfiguration,
  onSetDocumentLayoutConfiguration,
  readOnly = false,
}: OutputDocumentLayoutProps): JSX.Element {
  const onAddSection = (): void => {
    onSetDocumentLayoutConfiguration([
      ...documentLayoutConfiguration,
      {
        type: OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE,
        title: DEFAULT_NEW_SECTION_TITLE,
      },
    ]);
  };

  const onUpdateSectionTitle = (name: string, index: number): void => {
    const updatedDocumentLayoutConfiguration = [...documentLayoutConfiguration];
    updatedDocumentLayoutConfiguration[index] = {
      type: OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE,
      title: name,
    };
    onSetDocumentLayoutConfiguration(updatedDocumentLayoutConfiguration);
  };

  const onDeleteSection = (index: number): void => {
    onSetDocumentLayoutConfiguration(
      documentLayoutConfiguration.slice(0, index).concat(documentLayoutConfiguration.slice(index + 1))
    );
  };

  const onMoveItem = (
    source: PresentationalOutputDocumentLayoutDnDItem,
    target: PresentationalOutputDocumentLayoutDnDItem
  ): void => {
    const updatedDocumentLayoutConfiguration = immutableMove<OutputDocumentLayoutItem>(
      documentLayoutConfiguration,
      documentLayoutConfiguration[source.index],
      target.index
    );
    onSetDocumentLayoutConfiguration(updatedDocumentLayoutConfiguration);
  };

  return (
    <PresentationalOutputDocumentLayout
      documentLayoutConfiguration={documentLayoutConfiguration.map((item) =>
        item.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE
          ? item
          : {
              ...item,
              label:
                item.outputConfig.type === AnalysisOutputType.PLATE
                  ? item.outputConfig.edgeLabel
                  : item.outputConfig.outputName,
            }
      )}
      onAddSection={readOnly ? noop : onAddSection}
      onUpdateSectionTitle={readOnly ? noop : onUpdateSectionTitle}
      onDeleteSection={readOnly ? noop : onDeleteSection}
      onMoveItem={readOnly ? noop : onMoveItem}
      readOnly={readOnly}
    />
  );
}

const styles = StyleSheet.create({
  document: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 10,
    width: '100%',
    fontSize: '13px',
    borderTop: 'solid 1px',
    borderColor: GRAY,
  },
  title: {
    display: 'flex',
    fontSize: '13px',
  },
  canvasWrapper: {
    // DnDCanvas adds top and left padding which we don't want, so use negative margin to cancel it out
    marginLeft: -1 * DND_BLOCK_SPACING,
    marginTop: -1 * DND_BLOCK_SPACING,
  },
  deleteIcon: {
    marginLeft: '6px',
    marginRight: '6px',
    padding: '0px',
    color: RED_LIGHT,
  },
  documentContentIcon: {
    width: '13px',
    height: '13px',
  },
  dndIcon: {
    color: GRAY_DARK,
    width: '20px',
    height: '20px',
    marginLeft: '0px',
  },
  dndInteractiveIcon: {
    ':hover': {
      color: GRAY_DARKER,
      cursor: 'pointer',
    },
  },
});
